import { useState, useEffect } from 'react';
import { UploadFileInputProps } from './props';
import { showToast } from '../../../../../helpers/toast';
import { getFileSizeMB } from '../../../../../helpers/file';
import { useChatContext } from '../../../../../context/Chat/Chat';

const UploadFile = ({ idMessage }: UploadFileInputProps) => {
  const { setFileDescription, setUploadedFiles, setPendingUploadIdMessage } =
    useChatContext();

  const [hasUploaded, setHasUploaded] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  useEffect(() => {
    setUploadedFiles([]);
    setFileDescription('');
    setPendingUploadIdMessage(idMessage);
    setHasUploaded(false);
    setIsDraggingFile(false);
  }, [idMessage, setFileDescription, setPendingUploadIdMessage, setUploadedFiles]);

  const processSelectedFiles = (files: File[]) => {
    if (files.length === 0) return;

    setIsProcessing(true);
    const acceptedFileTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const size = getFileSizeMB(file.size);
      if (size > 50) {
        errors.push(`El archivo ${file.name} excede el límite de 50MB.`);
      } else if (!acceptedFileTypes.includes(file.type)) {
        errors.push(`El archivo ${file.name} no es un tipo permitido.`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      showToast(errors.join('\n'), 'error');
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      setPendingUploadIdMessage(idMessage);
    }

    setIsProcessing(false);
  };

  const onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (!files || files.length === 0) return;

    processSelectedFiles(Array.from(files));
    e.currentTarget.value = '';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing) {
      setIsDraggingFile(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!isProcessing) {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFile(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    const files = Array.from(e.dataTransfer.files);
    processSelectedFiles(files);
  };

  return (
    <>
      {!hasUploaded && (
        <>
          <input
            type="file"
            id={`uploadFileInput-${idMessage}`}
            accept=".pdf,.xls,.xlsx,.ppt,.pptx"
            className="invisible"
            multiple
            onChange={onChange}
          />

          <div className="upload-file-container row">
            <div
              className={`upload-file-label light-shadow${
                isDraggingFile ? ' upload-file-label--drag-active' : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isProcessing ? (
                <div className="spinner"></div>
              ) : (
                <>
                  <label
                    htmlFor={`uploadFileInput-${idMessage}`}
                    className="upload-file-prompt"
                  >
                    <div className="upload-file-img" aria-hidden="true"></div>
                    <span className="upload-file-title">
                      {isDraggingFile ? 'Suelta aquí' : 'Arrastra archivos'}
                    </span>
                    <span className="upload-file-hint">
                      PDF, Excel o PowerPoint · máx. 50 MB
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UploadFile;
